import { getGroupingClassNames } from "@myaccount/_components/my-group-members/_client/get-grouping-class-names";
import { useEmailSelection } from "@myaccount/_components/my-group-members/_client/hooks/use-email-selection";
import { TravelerNameCheckbox } from "../../traveler-name-checkbox/TravelerNameCheckbox";

import { useI18n } from "@wojo/localization";
import { AlertBanner, Table, Text } from "@wojo/ui";
import dayjs from "dayjs";
import {
    ConfirmedRequestedGroupMemberTripsFragment,
    GroupMembersDepartureFragment,
} from "generated/graphql";
import React from "react";
import { EmailTravelersButton } from "../../email-travelers-button/EmailTravelersButton";
import styles from "./FlightsTable.module.scss";

export type FlightsProps = {
    departure: GroupMembersDepartureFragment;
    bookedTravelers: ConfirmedRequestedGroupMemberTripsFragment[][];
    emailHelpTextId: string;
};

const formatFlightDates = (
    traveler: ConfirmedRequestedGroupMemberTripsFragment,
    displayDateFormat: string,
) => {
    if (traveler.isLandOnly) {
        return (
            <>
                <span aria-hidden>—</span>
                <span className="visually-hidden">Not applicable</span>
            </>
        );
    }
    return (
        <span className={styles.flightdates}>
            {traveler.openDepartingFlightLineItem ? (
                <span>
                    {dayjs(
                        traveler.openDepartingFlightLineItem.departureDate,
                    ).format(displayDateFormat)}
                </span>
            ) : (
                ""
            )}
            {traveler.openReturningFlightLineItem ? (
                <span>
                    {dayjs(
                        traveler.openReturningFlightLineItem.departureDate,
                    ).format(displayDateFormat)}
                </span>
            ) : (
                ""
            )}
        </span>
    );
};

const formatDepartureGateway = (
    traveler: ConfirmedRequestedGroupMemberTripsFragment,
    landOnlyText: string,
) => {
    const departureGateway =
        traveler.openDepartingFlightLineItem?.departureGateway;
    const departureGatewayCode =
        traveler.openDepartingFlightLineItem?.departureGatewayCode;
    const internalCityName = departureGateway?.internalCityName ?? "";
    if (traveler.isLandOnly) {
        return landOnlyText;
    }

    return `${internalCityName}, ${departureGateway?.stateCode ?? ""} (${
        departureGatewayCode ?? ""
    })`;
};

export const FlightsTable: React.FC<FlightsProps> = (props) => {
    const { bookedTravelers, departure, emailHelpTextId } = props;
    const i18n = useI18n("account").account.myGroupMembers.flightsTable;
    const { displayDateFormat } = useI18n("global").global;
    const flightsReleased = departure.flightsRelease?.isReleased;
    const flightsReleasedDate = departure.flightsRelease?.releaseDate;
    const { selectedEmailsMap, allEmails, handleCheckboxChange } =
        useEmailSelection({ confirmedTravelers: bookedTravelers });
    const selectedEmails = Array.from(selectedEmailsMap.values()).filter(
        (email) => email,
    );

    return (
        <>
            {flightsReleased ? (
                <AlertBanner
                    title={i18n.flightsReleasedTitle.l()}
                    status="success"
                    showBorder={true}
                    style={{ marginBottom: "var(--g-spacing-xl)" }}
                >
                    {i18n.flightsReleasedMessage.l()}
                </AlertBanner>
            ) : (
                <AlertBanner
                    title={i18n.flightsUnreleasedTitle.l()}
                    status="info"
                    showBorder={true}
                    style={{ marginBottom: "var(--g-spacing-xl)" }}
                >
                    {i18n.flightsUnreleasedMessage.l({
                        date: dayjs(flightsReleasedDate).format(
                            displayDateFormat.l(),
                        ),
                    })}
                </AlertBanner>
            )}
            <div className={styles.table}>
                <Table
                    aria-describedby={emailHelpTextId}
                    header={
                        <Table.Header>
                            <Table.Cell>{i18n.nameHeader.l()}</Table.Cell>
                            <Table.Cell className={styles["offset-checkbox"]}>
                                {i18n.departureHeader.l()}
                            </Table.Cell>

                            <Table.Cell className={styles["offset-checkbox"]}>
                                {i18n.flightDatesHeader.l()}
                            </Table.Cell>
                        </Table.Header>
                    }
                    showTitle={false}
                    tableTitle={
                        <Text.Heading
                            order="3"
                            style={{ paddingBottom: "var(--g-spacing-md)" }}
                        >
                            {i18n.tableTitle.l()}
                        </Text.Heading>
                    }
                    isStrictStyle={true}
                >
                    {bookedTravelers.map((group) =>
                        group.map((traveler, travelerIndex) => {
                            const email = traveler.customer?.email ?? "";
                            const classNames = getGroupingClassNames(
                                travelerIndex,
                                group.length,
                            );
                            return (
                                <Table.Row
                                    key={traveler._id}
                                    className={classNames}
                                    header={
                                        <Table.Cell className={classNames}>
                                            <TravelerNameCheckbox
                                                id={traveler._id}
                                                email={email}
                                                selectedEmailsMap={
                                                    selectedEmailsMap
                                                }
                                                handleEmailCheckboxChange={
                                                    handleCheckboxChange
                                                }
                                                firstName={
                                                    traveler.customer
                                                        ?.firstName ?? ""
                                                }
                                                lastName={
                                                    traveler.customer
                                                        ?.lastName ?? ""
                                                }
                                            />
                                        </Table.Cell>
                                    }
                                >
                                    <Table.Cell>
                                        {formatDepartureGateway(
                                            traveler,
                                            i18n.landOnlyText.l(),
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {formatFlightDates(
                                            traveler,
                                            displayDateFormat.l(),
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            );
                        }),
                    )}
                </Table>
            </div>
            <EmailTravelersButton
                emails={selectedEmails}
                allEmails={allEmails}
            />
        </>
    );
};
